import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "@/pages/about/components/ContactForm";
import { contact, site, testimonials } from "@/mocks/site";

const aboutHeroImage =
  "https://readdy.ai/api/search-image?query=conceptual%20editorial%20photograph%20of%20an%20old%20wooden%20archive%20shelf%20filled%20with%20labeled%20folders%20and%20a%20single%20warm%20desk%20lamp%2C%20soft%20dust%20in%20the%20air%2C%20desaturated%20rust%20and%20cream%20tones%2C%20quiet%20documentary%20atmosphere%2C%20fine%20film%20grain%2C%20no%20people&width=1200&height=800&seq=about-archive-01&orientation=landscape";

const corePrinciples = [
  {
    icon: "ri-shield-user-line",
    title: "当事人优先",
    desc: "所有亲述均在当事人充分知情并同意后刊发，以化名呈现并对可识别信息做模糊处理，并保留随时撤下的权利。",
  },
  {
    icon: "ri-file-text-line",
    title: "只陈述，不审判",
    desc: "我们记录发生了什么、当事人如何感受，但不对争议事件本身下结论，也不参与任何一方的舆论站队。",
  },
  {
    icon: "ri-heart-3-line",
    title: "面向恢复写作",
    desc: "内容的目的不是让读者沉浸在愤怒里，而是帮助正在经历的人找到可执行的方向，让围观者理解伤害如何发生。",
  },
  {
    icon: "ri-refresh-line",
    title: "长期更新与回访",
    desc: "我们会持续回访受访当事人，追踪他们数月甚至数年后的状态，让「伤害后果」不止停留在事发当下。",
  },
];

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `关于${site.name}`,
    inLanguage: "zh-CN",
    publisher: {
      "@type": "Organization",
      name: site.name,
      email: site.email,
      description: site.intro,
    },
  };

  return (
    <>
      <title>关于我们｜回声墙 网暴黑料亲历者档案的内容原则</title>
      <meta
        name="description"
        content="回声墙是一个关注网暴黑料受害者处境的纪实内容平台。了解我们的品牌定位、发展理念、内容原则、读者口碑，以及求助与联系渠道。"
      />
      <meta name="keywords" content="回声墙,网暴黑料,网暴黑料受害者,关于我们,内容原则" />
      <link rel="canonical" href="/about" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="关于我们｜回声墙 网暴黑料亲历者档案的内容原则" />
      <meta
        property="og:description"
        content="了解回声墙的品牌定位、内容原则与求助联系渠道。"
      />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <section className="bg-secondary-950 pt-28 md:pt-36 pb-14 md:pb-20">
        <div className="max-w-editorial mx-auto px-4 md:px-8">
          <div className="[&_a]:text-background-200/70 [&_span]:text-background-100/80">
            <Breadcrumb items={[{ label: "关于我们" }]} />
          </div>
          <h1 className="mt-6 font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight max-w-4xl">
            关于 {site.name}
          </h1>
          <p className="mt-5 max-w-3xl text-sm md:text-base leading-relaxed text-background-200/85">
            {site.slogan}。我们相信，伤害被准确地说出来，就已经开始被削弱。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <SectionHeading
                eyebrow="品牌介绍"
                title="一个专注于「网暴黑料」的档案型内容平台"
                description={`${site.name}成立于 ${site.foundedYear} 年，是一个${site.location}。`}
              />
              <div className="mt-6 flex flex-col gap-4 text-sm md:text-base leading-relaxed text-foreground-600">
                <p>
                  我们关注的不是热搜本身，而是热搜背后那个具体的人。当一段视频、一张截图、一份所谓「黑料」被反复转发时，
                  当事人的姓名、工作、家庭关系乃至日常生活，往往在几天之内被彻底打乱。而这些后果，很少被完整记录。
                </p>
                <p>
                  所以我们选择用长文的方式，把口述整理成可以被认真阅读的内容：他当时在做什么、看到第一条攻击时是什么反应、
                  求助之后又经历了什么。我们也会把心理影响、创伤阶段与求助渠道系统整理出来，让后来者少走一些弯路。
                </p>
                <p>
                  我们不提供猎奇的八卦，也不做情绪宣泄的扩音器。我们只希望，当一个普通人被「挂出来」的时候，
                  他至少能找到一份说明自己正在经历什么的材料。
                </p>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden bg-background-200 h-64 md:h-[460px]">
              <img
                src={aboutHeroImage}
                alt="摆放着编号档案册的木质书架与暖色台灯，象征回声墙对网暴黑料亲历者口述的归档整理"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="内容原则"
            title="我们怎样决定什么该被写下来"
            description="这四条原则贯穿我们每一篇档案的编辑过程，也是我们对受访当事人的基本承诺。"
            align="center"
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {corePrinciples.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg bg-background-50 border border-background-200 p-6"
              >
                <span className="shrink-0 w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <SectionHeading
            eyebrow="读者口碑"
            title="他们从这些文字里读到了什么"
            description="以下为读者与从业者来信节选，已获授权并作匿名处理。"
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="flex flex-col rounded-lg border border-background-200 bg-background-100/60 p-6"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.text}
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-background-200">
                  <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                  <span className="block text-xs text-foreground-500 mt-0.5">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="help" className="bg-background-100 border-t border-background-200">
        <div className="max-w-editorial mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="求助与联系"
                title="写信给我们，或者只是把话说完"
                description="无论是想讲述自己的经历、提供线索，还是想了解如何帮助身边的人，都欢迎来信。"
              />
              <ul className="mt-8 flex flex-col gap-5">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">编辑部邮箱</p>
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {contact.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-hand-heart-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">求助专用邮箱</p>
                    <a
                      href={`mailto:${contact.helpEmail}`}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {contact.helpEmail}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">回复时间</p>
                    <p className="text-sm text-foreground-600">{contact.responseTime}</p>
                  </div>
                </li>
              </ul>

              <p className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5 text-sm leading-relaxed text-primary-900">
                {contact.note}
              </p>
            </div>

            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>

          <div className="mt-12 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-base font-bold text-foreground-950">免责声明</h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              {site.name}所刊内容均基于当事人授权口述整理，人物姓名与可识别信息已作模糊处理，不对应任何具体的现实争议事件。
              本站提供的心理与法律相关表述仅用于一般性科普，不构成医疗诊断或法律意见。
              如你正处于危急状态，请立即联系当地紧急服务或专业心理援助机构。
            </p>
          </div>
        </div>
      </section>
    </>
  );
}